import React, { useState } from 'react';
import { Send, User, UserX } from 'lucide-react';
import StarRating from './StarRating';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Formulário para criar avaliação de curso
 */
const ReviewForm = ({ courseId, courseName, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Por favor, selecione uma nota de 1 a 5 estrelas');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/api/reviews`,
        {
          course_id: courseId,
          rating,
          comment: comment.trim() || null,
          is_anonymous: isAnonymous
        }
      );

      if (onReviewSubmitted) {
        onReviewSubmitted(response.data);
      }

      // Reset form
      setRating(0);
      setComment('');
      setIsAnonymous(false);

    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao enviar avaliação';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
      <h3 className="text-lg font-semibold text-white mb-4">
        Avaliar: {courseName}
      </h3>

      {/* Rating Stars */}
      <div className="mb-4">
        <label className="block text-sm text-gray-400 mb-2">Sua nota *</label>
        <StarRating 
          rating={rating} 
          size="lg" 
          interactive={true} 
          onRatingChange={setRating}
        />
      </div>

      {/* Comment */}
      <div className="mb-4">
        <label className="block text-sm text-gray-400 mb-2">
          Comentário (opcional)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={500}
          rows={4}
          placeholder="Conte sua experiência com o curso..."
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
        />
        <div className="text-xs text-gray-500 mt-1 text-right">
          {comment.length}/500 caracteres
        </div>
      </div>

      {/* Anonymous Toggle */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setIsAnonymous(!isAnonymous)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            isAnonymous 
              ? 'bg-zinc-700 border-zinc-600 text-white' 
              : 'bg-transparent border-zinc-700 text-gray-400 hover:border-zinc-600'
          }`}
        >
          {isAnonymous ? (
            <>
              <UserX className="w-4 h-4" />
              Avaliação anônima
            </>
          ) : (
            <>
              <User className="w-4 h-4" />
              Mostrar meu nome
            </>
          )}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          {isAnonymous 
            ? 'Seu nome não será exibido publicamente' 
            : 'Seu nome aparecerá junto à avaliação'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || rating === 0}
        className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Enviar Avaliação
          </>
        )}
      </button>
    </form>
  );
};

export default ReviewForm;
